import React, { useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './components/auth/Login';
import LoginMobileOtp from './components/auth/LoginMobileOtp';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Dashboard from './components/Dashboard';
import AnalysisList from './components/AnalysisList';
import AnalysisDetail from './components/AnalysisDetail';
import CreateAnalysis from './components/CreateAnalysis';
import BidDomainPage from './components/BidDomainPage';
import FloatingCalculator, { readInitialCalculatorOpen } from './components/tools/FloatingCalculator';
import AmountCalculator from './components/tools/AmountCalculator';

const SIDEBAR_COLLAPSED_KEY = 'pa-sidebar-collapsed';

const DesktopLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [calculatorOpen, setCalculatorOpen] = useState(() => readInitialCalculatorOpen());
  const [amountCalcOpen, setAmountCalcOpen] = useState(false);
  const location = useLocation();
  const isAnalysisDetail = /^\/analyses\/[^/]+$/.test(location.pathname) && !location.pathname.endsWith('/create');
  const isFullBleed = isAnalysisDetail || location.pathname === '/bid-domain';

  const toggleCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {}
      return next;
    });
  };

  return (
    <div className="project-analysis-panel-bg flex h-screen max-h-[100dvh] text-slate-800 overflow-hidden safe-area-inset" dir="rtl">
      <Sidebar
        open={sidebarOpen}
        collapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapsed={toggleCollapsed}
        onOpenCalculator={() => setCalculatorOpen(true)}
        onOpenAmountCalculator={() => setAmountCalcOpen(true)}
      />
      <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden bg-transparent">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <div
          className={`flex-1 min-h-0 main-scroll-area flex flex-col ${
            isFullBleed ? 'p-0 overflow-hidden' : 'overflow-y-auto overflow-x-hidden px-3 py-3 lg:px-6 lg:py-5'
          }`}
        >
          <Outlet />
        </div>
      </main>
      <FloatingCalculator open={calculatorOpen} onOpenChange={setCalculatorOpen} />
      <AmountCalculator open={amountCalcOpen} onOpenChange={setAmountCalcOpen} />
    </div>
  );
};

const App: React.FC = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/login-otp" element={<LoginMobileOtp />} />
    <Route
      element={
        <ProtectedRoute>
          <Outlet />
        </ProtectedRoute>
      }
    >
      <Route element={<DesktopLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="analyses" element={<AnalysisList />} />
        <Route path="analyses/create" element={<CreateAnalysis />} />
        <Route path="analyses/:id" element={<AnalysisDetail />} />
        <Route path="bid-domain" element={<BidDomainPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Route>
  </Routes>
);

export default App;
